/**
 * 위젯 UI i18n — 위젯 크롬(입력창·버튼·메뉴·피드백·가이드·에러 등 '우리가 쓴' 고정 문구)을
 * 손님 브라우저 언어에 맞춘다. 손님이 쓴 콘텐츠(테넌트 config: greeting·추천질문·headerTitle 등)는
 * 번역하지 않는다(사장님 원문 유지). 서버 챗 파이프라인의 언어 미러링과 짝.
 *
 * 지원: ko·en·ja·zh. 언어 결정: config.widget.lang(테넌트 강제) → navigator.language.
 * 한국어 브라우저는 기존과 100% 동일(회귀 없음). 미지원 언어는 en 우선 폴백.
 */
export type WLang = 'ko' | 'en' | 'ja' | 'zh';

type Entry = Record<WLang, string>;

const DICT = {
  buttonLabel: { ko: '문의', en: 'Chat', ja: '相談', zh: '咨询' },
  openAria: { ko: 'AI 직원 상담 열기', en: 'Open chat', ja: 'チャットを開く', zh: '打开聊天' },
  closeAria: { ko: 'AI 직원 상담 닫기', en: 'Close chat', ja: 'チャットを閉じる', zh: '关闭聊天' },
  menuAria: { ko: '메뉴', en: 'Menu', ja: 'メニュー', zh: '菜单' },
  inputPlaceholder: { ko: '메시지를 입력하세요...', en: 'Type a message...', ja: 'メッセージを入力...', zh: '输入消息...' },
  send: { ko: '전송', en: 'Send', ja: '送信', zh: '发送' },
  guide: { ko: '이용 가이드', en: 'How to use', ja: 'ご利用ガイド', zh: '使用指南' },
  reset: { ko: '대화 내용 초기화', en: 'Clear conversation', ja: '会話をリセット', zh: '清空对话' },
  privacy: { ko: '개인정보 처리방침', en: 'Privacy policy', ja: 'プライバシーポリシー', zh: '隐私政策' },
  fontSizeLabel: { ko: '글자 크기', en: 'Text size', ja: '文字サイズ', zh: '字体大小' },
  fsNormal: { ko: '보통', en: 'Normal', ja: '標準', zh: '标准' },
  fsLarge: { ko: '크게', en: 'Large', ja: '大', zh: '大' },
  fsXLarge: { ko: '아주 크게', en: 'Extra large', ja: '特大', zh: '特大' },
  feedbackAsk: { ko: '도움이 되었나요?', en: 'Was this helpful?', ja: 'お役に立ちましたか？', zh: '有帮助吗？' },
  feedbackYes: { ko: '도움돼요', en: 'Helpful', ja: '役立った', zh: '有帮助' },
  feedbackNo: { ko: '아쉬워요', en: 'Not helpful', ja: '残念', zh: '没帮助' },
  badge: { ko: ' 스테이톡 AI 이용중', en: ' Powered by STAYTOQ AI', ja: ' STAYTOQ AI 提供', zh: ' 由 STAYTOQ AI 提供' },
  ctaDefault: { ko: '예약하기', en: 'Book now', ja: '予約する', zh: '立即预订' },
  // 「확정」이라 쓰지 않는다 — 누르면 예약 페이지로 갈 뿐이고, 확정은 그 페이지의 결제·승인이다(2026-10-22 사용자 결정).
  ctaConfirm: { ko: '예약하러 가기', en: 'Go to booking', ja: '予約へ進む', zh: '前往预订' },
  bookCardTitle: { ko: '예약 정보', en: 'Booking details', ja: 'ご予約内容', zh: '预订信息' },
  handoffCall: { ko: '전화 연결', en: 'Call us', ja: '電話する', zh: '电话联系' },
  handoffInquiry: { ko: '문의 남기기', en: 'Leave an inquiry', ja: 'お問い合わせ', zh: '留言咨询' },
  bookLead: {
    ko: '아래에서 바로 예약하실 수 있어요 😊',
    en: 'You can book right here 😊',
    ja: '下記からすぐにご予約いただけます 😊',
    zh: '您可以在下方直接预订 😊',
  },
  bubbleError: {
    ko: '오류가 발생했어요. 잠시 후 다시 시도해주세요.',
    en: 'Something went wrong. Please try again in a moment.',
    ja: 'エラーが発生しました。しばらくしてからもう一度お試しください。',
    zh: '出错了，请稍后再试。',
  },
  // 업체 전화번호는 문구에 박지 않는다 — 예전엔 한국어에만 특정 업체 번호가 들어 있어
  // **모든 업체 손님이 그 번호로 안내받았다**(2026-09-04 점검). 번호가 있을 때만 아래를 덧붙인다.
  streamTimeoutCall: {
    ko: '급하시면 전화 주세요:', en: 'Or call us:', ja: 'お急ぎの方はお電話ください:', zh: '着急的话请致电:',
  },
  streamTimeout: {
    ko: '죄송합니다. 응답이 지연되고 있어요. 잠시 후 다시 시도해 주세요 🙏',
    en: 'Sorry, the response is taking longer than usual. Please try again shortly, or contact us by phone 🙏',
    ja: '申し訳ございません。応答に時間がかかっています。しばらくしてからもう一度お試しいただくか、お電話にてお問い合わせください 🙏',
    zh: '抱歉，响应时间较长。请稍后再试，或致电咨询 🙏',
  },
  streamError: {
    ko: '죄송합니다. 일시적인 오류가 발생했어요. 잠시 후 다시 시도해 주세요 🙏',
    en: 'Sorry, a temporary error occurred. Please try again in a moment 🙏',
    ja: '申し訳ございません。一時的なエラーが発生しました。しばらくしてからもう一度お試しください 🙏',
    zh: '抱歉，发生了临时错误。请稍后再试 🙏',
  },
  suggShow: { ko: '추천 질문 {n}개 펼치기', en: 'Show {n} suggested questions', ja: 'おすすめの質問 {n}件を表示', zh: '显示 {n} 个推荐问题' },
  suggHide: { ko: '추천 질문 숨기기', en: 'Hide suggested questions', ja: 'おすすめの質問を隠す', zh: '隐藏推荐问题' },
  guideClose: { ko: '가이드 닫기', en: 'Close guide', ja: 'ガイドを閉じる', zh: '关闭指南' },
  skip: { ko: '건너뛰기', en: 'Skip', ja: 'スキップ', zh: '跳过' },
  next: { ko: '다음', en: 'Next', ja: '次へ', zh: '下一步' },
  start: { ko: '시작하기', en: 'Get started', ja: 'はじめる', zh: '开始' },
  pauseAria: { ko: '자동 넘김 일시정지', en: 'Pause auto-advance', ja: '自動送りを一時停止', zh: '暂停自动播放' },
  typing: { ko: '답변 작성 중', en: 'Typing', ja: '入力中', zh: '正在输入' },
  // 처리 단계 한 줄(첫 글자 전) — 서버가 그 일을 시작할 때 보낸다(src/lib/chat-stages.ts).
  stageIntent: { ko: '의도 파악 중', en: 'Understanding your question', ja: 'ご質問を確認中', zh: '正在理解您的问题' },
  stageHistory: { ko: '대화 기록 확인 중', en: 'Checking our conversation', ja: '会話履歴を確認中', zh: '正在查看对话记录' },
  stagePrepare: { ko: '질문 준비 중', en: 'Preparing your question', ja: '質問を準備中', zh: '正在整理问题' },
  stageKnowledge: { ko: '지식 확인 중', en: 'Checking information', ja: '情報を確認中', zh: '正在查找信息' },
  stageAvailability: { ko: '예약 가능 여부 확인 중', en: 'Checking availability', ja: '空室状況を確認中', zh: '正在查询可预订情况' },
  prev: { ko: '이전', en: 'Previous', ja: '前へ', zh: '上一步' },
  historyBtn: { ko: '이전 대화', en: 'Past chats', ja: '過去の会話', zh: '历史对话' },
  resumeTitle: { ko: '지난 대화가 있어요', en: 'You have a past chat', ja: '前回の会話があります', zh: '您有之前的对话' },
  resumeBtn: { ko: '이어보기', en: 'View', ja: '表示', zh: '查看' },
  statusAi: { ko: 'AI가 바로 답해요', en: 'AI replies right away', ja: 'AIがすぐにお答えします', zh: 'AI 即时回复' },
  statusHandoff: { ko: '필요하면 담당자에게 연결해 드려요', en: 'we can connect you to staff', ja: '必要なら担当者におつなぎします', zh: '需要时可为您转接工作人员' },
  historyLoading: { ko: '불러오는 중…', en: 'Loading…', ja: '読み込み中…', zh: '加载中…' },
  historyEmpty: { ko: '이전 대화가 없어요', en: 'No past chats', ja: '過去の会話はありません', zh: '暂无历史对话' },
  historyErr: { ko: '불러오지 못했어요', en: "Couldn't load", ja: '読み込めませんでした', zh: '加载失败' },
  historyLabel: { ko: '이전 대화', en: 'Previous conversation', ja: '過去の会話', zh: '历史对话' },
  today: { ko: '오늘', en: 'Today', ja: '今日', zh: '今天' },
  yesterday: { ko: '어제', en: 'Yesterday', ja: '昨日', zh: '昨天' },
  nthCategory: { ko: '{n}번째 카테고리', en: 'Category {n}', ja: 'カテゴリ {n}', zh: '第 {n} 个分类' },
  fallbackGreeting: {
    ko: '안녕하세요. 무엇을 도와드릴까요?',
    en: 'Hello! How can I help you today?',
    ja: 'こんにちは。ご用件をお伺いします。',
    zh: '您好，有什么可以帮您？',
  },
  botName: { ko: 'AI 직원', en: 'AI staff', ja: 'AIスタッフ', zh: 'AI工作人员' },
  langLabel: { ko: '언어', en: 'Language', ja: '言語', zh: '语言' },
} satisfies Record<string, Entry>;

export type WKey = keyof typeof DICT;

export function pickWidgetLang(navLang?: string, override?: unknown): WLang {
  if (override === 'ko' || override === 'en' || override === 'ja' || override === 'zh') return override;
  const n = (navLang || '').toLowerCase();
  if (n.startsWith('ko')) return 'ko';
  if (n.startsWith('ja')) return 'ja';
  if (n.startsWith('zh')) return 'zh';
  return 'en';
}

export function makeT(lang: WLang) {
  return (key: WKey, vars?: Record<string, string | number>): string => {
    let s = DICT[key]?.[lang] ?? DICT[key]?.en ?? DICT[key]?.ko ?? String(key);
    if (vars) for (const k in vars) s = s.split(`{${k}}`).join(String(vars[k]));
    return s;
  };
}

export type WT = ReturnType<typeof makeT>;

// 이용 가이드 4페이지 — 손님 언어별. cfg.name(테넌트명)만 보간. 숙박 특화 예시는 비한국어에선 범용화.
export type GuideSeg = { t: string; hl?: boolean };
/** `icon` 은 `UI_ICON_PATHS`(ui.ts)의 키다 — 이모지였던 자리(2026-09-20 교체). */
export type GuidePage = { segs: GuideSeg[]; icon: string; cap: string };

export function guidePagesFor(lang: WLang, name: string): GuidePage[] {
  if (lang === 'ja') {
    return [
      { segs: [{ t: 'ご質問に' }, { t: 'すぐ回答', hl: true }, { t: '\nいたします' }], icon: 'messages-square',
        cap: 'ご利用時間、料金、施設、使い方、キャンセル規定、周辺スポットまで — よくあるご質問にAIが即座にお答えします。' },
      { segs: [{ t: '' }, { t: 'おすすめの質問', hl: true }, { t: 'をタップ\nまたは自由に入力' }], icon: 'mouse-pointer-click',
        cap: '下のおすすめの質問をタップすると、すぐに回答が表示されます。ご希望の質問がなければ、自由に入力してください。' },
      { segs: [{ t: '' }, { t: '24時間', hl: true }, { t: 'いつでも\nお気軽にどうぞ' }], icon: 'moon',
        cap: `深夜でも週末でも、${name}のAIが休まず対応します。複雑なお問い合わせはお電話へのご案内もいたします。` },
      { segs: [{ t: '' }, { t: 'メニュー', hl: true }, { t: 'から文字サイズや\n会話リセットを設定' }], icon: 'sliders-horizontal',
        cap: '左下のメニュー → 文字サイズ（標準・大・特大）、会話リセット、プライバシーポリシーをご確認いただけます。' },
    ];
  }
  if (lang === 'zh') {
    return [
      { segs: [{ t: '为您的问题' }, { t: '即时解答', hl: true }], icon: 'messages-square',
        cap: '营业时间、价格、设施、使用方法、退款政策、周边景点 — AI 会立即回答常见问题。' },
      { segs: [{ t: '点击' }, { t: '推荐问题', hl: true }, { t: '\n或直接输入' }], icon: 'mouse-pointer-click',
        cap: '点击下方推荐问题即可立即查看答案。如果没有您需要的问题，请直接输入。' },
      { segs: [{ t: '' }, { t: '24小时', hl: true }, { t: '随时\n欢迎咨询' }], icon: 'moon',
        cap: `无论深夜还是周末，${name} 的 AI 都会全天候为您服务。复杂问题也会引导您电话联系。` },
      { segs: [{ t: '在' }, { t: '菜单', hl: true }, { t: '中设置字体大小\n和清空对话' }], icon: 'sliders-horizontal',
        cap: '左下角的菜单 → 字体大小（标准·大·特大）、清空对话、隐私政策。' },
    ];
  }
  if (lang === 'en') {
    return [
      { segs: [{ t: 'Get ' }, { t: 'instant answers', hl: true }, { t: '\nto your questions' }], icon: 'messages-square',
        cap: 'Hours, rates, facilities, how-to, refund policy, nearby spots — our AI answers frequently asked questions right away.' },
      { segs: [{ t: 'Tap a ' }, { t: 'suggested question', hl: true }, { t: '\nor just type your own' }], icon: 'mouse-pointer-click',
        cap: 'Tap a suggested question below to see the answer instantly. If you don’t see what you need, just type it in.' },
      { segs: [{ t: 'Ask anytime, ' }, { t: '24/7', hl: true }], icon: 'moon',
        cap: `Day or night, ${name}’s AI is always here to help. For complex questions, we’ll guide you to a phone contact too.` },
      { segs: [{ t: 'Use the ' }, { t: 'menu', hl: true }, { t: ' for text size\nand to clear the chat' }], icon: 'sliders-horizontal',
        cap: 'The bottom-left menu → text size (Normal · Large · Extra large), clear conversation, and privacy policy.' },
    ];
  }
  return [
    { segs: [{ t: '이런 것들을 ' }, { t: '바로 답변', hl: true }, { t: '\n받을 수 있어요' }], icon: 'messages-square',
      cap: '체크인·체크아웃 시간, 바비큐·수영장 이용법, 객실 요금, 환불 규정, 주변 관광지까지 — 자주 묻는 질문에 AI가 즉시 안내합니다.' },
    { segs: [{ t: '' }, { t: '추천 질문', hl: true }, { t: '을 탭하거나\n직접 입력하세요' }], icon: 'mouse-pointer-click',
      cap: '아래 추천 질문 버튼을 누르면 바로 답변을 볼 수 있어요. 원하는 질문이 없으면 입력창에 자유롭게 물어보세요.' },
    { segs: [{ t: '' }, { t: '24시간', hl: true }, { t: ' 언제든\n편하게 물어보세요' }], icon: 'moon',
      cap: `새벽이든 주말이든 ${name} AI가 쉬지 않고 응대합니다. 복잡한 문의는 전화 연결도 안내해 드려요.` },
    { segs: [{ t: '' }, { t: '메뉴', hl: true }, { t: '에서 글자 크기와\n대화 초기화를 설정' }], icon: 'sliders-horizontal',
      cap: '왼쪽 아래 메뉴 → 글자 크기(보통·크게·아주 크게), 대화 초기화, 개인정보 처리방침을 확인할 수 있습니다.' },
  ];
}
